import React from 'react';
import {
  Search, Plus, Save, Loader2, X, Edit2, Trash2, Upload, Eye,
  ChevronRight, ChevronDown, MapPin, Download, FileText, CheckCircle, User
} from 'lucide-react';
import { StatusBadge } from './FinanceDataTabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RECEIPT_FILENAME_MAX_LENGTH = 15;

export const FinanceTripsTab = ({ ctx }) => {
  const {
    trips, tripFilters, setTripFilters, showTripForm, setShowTripForm,
    tripForm, setTripForm, editingTrip, resetTripForm, handleSaveTrip,
    handleEditTrip, handleDeleteTrip, handleTripReceiptUpload, handleExport,
    expandedTrip, setExpandedTrip, peopleInput, setPeopleInput,
    saving, inputClass, labelClass
  } = ctx;

  return (
    <div className="space-y-4" data-testid="trips-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input type="text" placeholder="Search trips..." value={tripFilters.search}
              onChange={(e) => setTripFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-48" data-testid="trip-search" />
          </div>
          <input type="date" value={tripFilters.date_from} onChange={(e) => setTripFilters(f => ({ ...f, date_from: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" placeholder="From" data-testid="trip-date-from" />
          <input type="date" value={tripFilters.date_to} onChange={(e) => setTripFilters(f => ({ ...f, date_to: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" placeholder="To" data-testid="trip-date-to" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('trips')} className="flex items-center gap-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]" data-testid="export-trips-btn">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => { resetTripForm(); setShowTripForm(true); }} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420]" data-testid="add-trip-btn">
            <Plus className="w-4 h-4" /> Log Trip
          </button>
        </div>
      </div>

      {showTripForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="trip-form">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading font-semibold text-sm">{editingTrip ? 'Edit Trip' : 'New Trip'}</h3>
            <button onClick={resetTripForm}><X className="w-5 h-5 text-[#4B5563]" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={labelClass}>Date *</label><input type="date" value={tripForm.date} onChange={(e) => setTripForm(f => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="trip-date" /></div>
            <div><label className={labelClass}>Destination *</label><input type="text" value={tripForm.destination} onChange={(e) => setTripForm(f => ({ ...f, destination: e.target.value }))} className={inputClass} placeholder="Where did you go?" data-testid="trip-destination" /></div>
            <div><label className={labelClass}>Starting Location</label><input type="text" value={tripForm.starting_location} onChange={(e) => setTripForm(f => ({ ...f, starting_location: e.target.value }))} className={inputClass} placeholder="Starting from..." data-testid="trip-starting" /></div>
            <div><label className={labelClass}>Miles Traveled</label><input type="number" step="0.1" value={tripForm.miles_traveled} onChange={(e) => setTripForm(f => ({ ...f, miles_traveled: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="trip-miles" /></div>
            <div><label className={labelClass}>People Involved</label><input type="text" value={peopleInput} onChange={(e) => { setPeopleInput(e.target.value); setTripForm(f => ({ ...f, people_involved: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })); }} className={inputClass} placeholder="Names, comma-separated" data-testid="trip-people" /></div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={tripForm.reimbursement_owed} onChange={(e) => setTripForm(f => ({ ...f, reimbursement_owed: e.target.checked }))} className="w-4 h-4 rounded border-[#E5E7EB] text-[#BA1826] focus:ring-[#BA1826]" data-testid="trip-reimb-owed" />
                <span className="text-sm text-[#4B5563]">Reimbursement Owed</span>
              </label>
            </div>
            <div className="md:col-span-3"><label className={labelClass}>Purpose / Mission *</label><textarea value={tripForm.purpose} onChange={(e) => setTripForm(f => ({ ...f, purpose: e.target.value }))} className={inputClass} rows={2} placeholder="Why was this trip taken?" data-testid="trip-purpose" /></div>
            <div className="md:col-span-3 border-t border-[#E5E7EB] pt-4 mt-2">
              <h4 className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-3">Trip Documentation</h4>
            </div>
            <div className="md:col-span-3"><label className={labelClass}>Visit Notes</label><textarea value={tripForm.visit_notes} onChange={(e) => setTripForm(f => ({ ...f, visit_notes: e.target.value }))} className={inputClass} rows={3} placeholder="General notes from the visit..." data-testid="trip-visit-notes" /></div>
            <div className="md:col-span-3"><label className={labelClass}>Meeting Notes</label><textarea value={tripForm.meeting_notes} onChange={(e) => setTripForm(f => ({ ...f, meeting_notes: e.target.value }))} className={inputClass} rows={3} placeholder="Who did you meet with?" data-testid="trip-meeting-notes" /></div>
            <div className="md:col-span-3"><label className={labelClass}>Needs Identified</label><textarea value={tripForm.needs_identified} onChange={(e) => setTripForm(f => ({ ...f, needs_identified: e.target.value }))} className={inputClass} rows={2} placeholder="What needs were identified?" data-testid="trip-needs" /></div>
            <div className="md:col-span-3"><label className={labelClass}>Mission Support</label><textarea value={tripForm.mission_support} onChange={(e) => setTripForm(f => ({ ...f, mission_support: e.target.value }))} className={inputClass} rows={2} placeholder="How did this trip support the mission?" data-testid="trip-mission-support" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSaveTrip} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="save-trip-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingTrip ? 'Update' : 'Save'}
            </button>
            <button onClick={resetTripForm} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {trips.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#9CA3AF]">No trips logged yet.</div>
        ) : trips.map((t) => (
          <div key={t.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`trip-card-${t.id}`}>
            <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#FAFAFA]" onClick={() => setExpandedTrip(expandedTrip === t.id ? null : t.id)}>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#BA1826]" />
                  <span className="text-sm font-medium">{t.destination}</span>
                </div>
                <span className="text-xs text-[#9CA3AF]">{t.date}</span>
                {t.miles_traveled > 0 && <span className="text-xs bg-[#F1F5F9] px-2 py-0.5 rounded">{t.miles_traveled} mi</span>}
                {t.reimbursement_owed && <StatusBadge status="owed" />}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); handleEditTrip(t); }} className="p-1 text-[#4B5563] hover:text-[#BA1826]"><Edit2 className="w-4 h-4" /></button>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteTrip(t.id); }} className="p-1 text-[#4B5563] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                {expandedTrip === t.id ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
              </div>
            </div>
            {expandedTrip === t.id && (
              <div className="border-t border-[#E5E7EB] p-4 space-y-3 bg-[#FAFAFA]">
                <div className="grid md:grid-cols-2 gap-4">
                  {t.starting_location && <div><span className="text-xs font-medium text-[#4B5563]">From:</span> <span className="text-sm">{t.starting_location}</span></div>}
                  {t.people_involved?.length > 0 && <div><span className="text-xs font-medium text-[#4B5563]">People:</span> <span className="text-sm">{t.people_involved.join(', ')}</span></div>}
                </div>
                <div><span className="text-xs font-medium text-[#4B5563] block mb-1">Purpose / Mission:</span><p className="text-sm text-[#374151] whitespace-pre-wrap">{t.purpose}</p></div>
                {t.visit_notes && <div><span className="text-xs font-medium text-[#4B5563] block mb-1">Visit Notes:</span><p className="text-sm text-[#374151] whitespace-pre-wrap">{t.visit_notes}</p></div>}
                {t.meeting_notes && <div><span className="text-xs font-medium text-[#4B5563] block mb-1">Meeting Notes:</span><p className="text-sm text-[#374151] whitespace-pre-wrap">{t.meeting_notes}</p></div>}
                {t.needs_identified && <div><span className="text-xs font-medium text-[#4B5563] block mb-1">Needs Identified:</span><p className="text-sm text-[#374151] whitespace-pre-wrap">{t.needs_identified}</p></div>}
                {t.mission_support && <div><span className="text-xs font-medium text-[#4B5563] block mb-1">Mission Support:</span><p className="text-sm text-[#374151] whitespace-pre-wrap">{t.mission_support}</p></div>}
                <div className="border-t border-[#E5E7EB] pt-3 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[#4B5563]">Receipts ({t.receipt_files?.length || 0})</span>
                    <label className="cursor-pointer text-[#BA1826] hover:underline text-xs flex items-center gap-1">
                      <Upload className="w-3 h-3" /> Add Receipt
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.webp"
                        onChange={(e) => { if (e.target.files[0]) handleTripReceiptUpload(t.id, e.target.files[0]); }} />
                    </label>
                  </div>
                  {t.receipt_files?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {t.receipt_files.map((f) => (
                        <a key={f} href={`${BACKEND_URL}/api/uploads/${f}`} target="_blank" rel="noreferrer" className="text-xs bg-white border border-[#E5E7EB] px-2 py-1 rounded hover:border-[#BA1826] flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {f.split('_').pop().substring(0, RECEIPT_FILENAME_MAX_LENGTH)}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const FinanceTeamTab = ({ ctx }) => {
  const {
    teamMembers, showTeamForm, setShowTeamForm, teamForm, setTeamForm,
    handleCreateTeamMember, handleToggleTeamMember, handleDeleteTeamMember,
    saving, inputClass, labelClass
  } = ctx;

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#4B5563]">Manage team member accounts for expense submissions.</p>
        <button onClick={() => setShowTeamForm(!showTeamForm)} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420]" data-testid="add-team-member-btn">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {showTeamForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="team-form">
          <h3 className="font-heading font-semibold text-sm mb-4">New Team Member</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={labelClass}>Name *</label><input type="text" value={teamForm.name} onChange={(e) => setTeamForm(f => ({ ...f, name: e.target.value }))} className={inputClass} placeholder="Full name" data-testid="team-name" /></div>
            <div><label className={labelClass}>Email *</label><input type="email" value={teamForm.email} onChange={(e) => setTeamForm(f => ({ ...f, email: e.target.value }))} className={inputClass} placeholder="email@example.com" data-testid="team-email" /></div>
            <div><label className={labelClass}>Password *</label><input type="password" value={teamForm.password} onChange={(e) => setTeamForm(f => ({ ...f, password: e.target.value }))} className={inputClass} placeholder="Set password" data-testid="team-password" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreateTeamMember} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="save-team-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Create Account
            </button>
            <button onClick={() => { setShowTeamForm(false); setTeamForm({ name: '', email: '', password: '' }); }} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-3">Team members can log in at <strong>/admin/team/login</strong> to submit expenses and trips for review.</p>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full" data-testid="team-table">
          <thead className="bg-[#F8F9FA]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Email</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-[#4B5563]">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Created</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-[#4B5563]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#9CA3AF]">No team members yet. Create accounts for your team to submit expenses.</td></tr>
            ) : teamMembers.map((m) => (
              <tr key={m.user_id} className="border-t border-[#E5E7EB]">
                <td className="px-4 py-3 text-sm font-medium">{m.name}</td>
                <td className="px-4 py-3 text-sm text-[#4B5563]">{m.email}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {m.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-[#9CA3AF]">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => handleToggleTeamMember(m.user_id, m.is_active !== false)}
                      className={`text-xs px-2 py-1 rounded ${m.is_active !== false ? 'text-yellow-700 hover:bg-yellow-50' : 'text-green-700 hover:bg-green-50'}`}>
                      {m.is_active !== false ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => handleDeleteTeamMember(m.user_id)} className="p-1 text-[#4B5563] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const FinanceSubmissionsTab = ({ ctx }) => {
  const {
    submissions, submissionFilters, setSubmissionFilters,
    reviewingSubmission, setReviewingSubmission, reviewForm, setReviewForm,
    handleReviewSubmission, saving, inputClass, labelClass
  } = ctx;

  return (
    <div className="space-y-4" data-testid="submissions-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <select value={submissionFilters.status} onChange={(e) => setSubmissionFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="submission-filter-status">
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {reviewingSubmission && (
        <div className="bg-white border-2 border-[#BA1826] rounded-lg p-6" data-testid="review-form">
          <h3 className="font-heading font-semibold text-sm mb-4">Review Submission</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={labelClass}>Decision *</label>
              <select value={reviewForm.status} onChange={(e) => setReviewForm(f => ({ ...f, status: e.target.value }))} className={inputClass} data-testid="review-status">
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
            <div><label className={labelClass}>Review Notes</label><textarea value={reviewForm.review_notes} onChange={(e) => setReviewForm(f => ({ ...f, review_notes: e.target.value }))} className={inputClass} rows={2} data-testid="review-notes" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleReviewSubmission} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="submit-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Submit Review
            </button>
            <button onClick={() => setReviewingSubmission(null)} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {submissions.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#9CA3AF]">No team submissions yet.</div>
        ) : submissions.map((s) => (
          <div key={s.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`submission-${s.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center">
                  <User className="w-4 h-4 text-[#4B5563]" />
                </div>
                <div>
                  <p className="text-sm font-medium">{s.submitted_by_name}</p>
                  <p className="text-xs text-[#9CA3AF]">{s.submitted_by_email} - {new Date(s.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={s.status} />
                <span className="text-xs bg-[#F1F5F9] px-2 py-0.5 rounded capitalize">{s.submission_type}</span>
              </div>
            </div>
            {s.submission_type === 'expense' && s.expense_data && (
              <div className="bg-[#FAFAFA] rounded p-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-[#9CA3AF]">Amount:</span> <span className="font-medium">${s.expense_data.amount}</span></div>
                <div><span className="text-[#9CA3AF]">Category:</span> {s.expense_data.category}</div>
                <div><span className="text-[#9CA3AF]">Date:</span> {s.expense_data.date}</div>
                <div><span className="text-[#9CA3AF]">Paid By:</span> {s.expense_data.paid_by}</div>
                {s.expense_data.description && <div className="col-span-2 md:col-span-4"><span className="text-[#9CA3AF]">Description:</span> {s.expense_data.description}</div>}
              </div>
            )}
            {s.submission_type === 'trip' && s.trip_data && (
              <div className="bg-[#FAFAFA] rounded p-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-[#9CA3AF]">Destination:</span> {s.trip_data.destination}</div>
                <div><span className="text-[#9CA3AF]">Date:</span> {s.trip_data.date}</div>
                <div><span className="text-[#9CA3AF]">Miles:</span> {s.trip_data.miles_traveled}</div>
                <div><span className="text-[#9CA3AF]">Purpose:</span> {s.trip_data.purpose}</div>
              </div>
            )}
            {s.receipt_file && (
              <a href={`${BACKEND_URL}/api/uploads/${s.receipt_file}`} target="_blank" rel="noreferrer" className="text-[#BA1826] text-xs hover:underline flex items-center gap-1 mb-2">
                <Eye className="w-3 h-3" /> View Receipt
              </a>
            )}
            {s.notes && <p className="text-xs text-[#4B5563] mb-2">Note: {s.notes}</p>}
            {s.reviewed_by && (
              <div className="text-xs text-[#9CA3AF] border-t border-[#E5E7EB] pt-2 mt-2">
                Reviewed by {s.reviewed_by} on {s.reviewed_at ? new Date(s.reviewed_at).toLocaleDateString() : ''}
                {s.review_notes && <span> - {s.review_notes}</span>}
              </div>
            )}
            {s.status === 'submitted' && (
              <div className="border-t border-[#E5E7EB] pt-2 mt-2">
                <button onClick={() => { setReviewingSubmission(s.id); setReviewForm({ status: 'approved', review_notes: '' }); }}
                  className="text-xs px-3 py-1 bg-[#BA1826] text-white rounded hover:bg-[#9A1420]" data-testid={`review-btn-${s.id}`}>
                  Review
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
